import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ShowHideColumnsButton,
    MRT_ToggleFiltersButton,
    MRT_TablePagination,
    MRT_TableContainer
} from "material-react-table/";
import {
    Box,
    useTheme,
    Typography,
    Button,
    Checkbox,
    Badge,
    alpha
} from "@mui/material/";
import tableStyles, { defaultMessages } from "../MaterialReactTable/styles";
import { strings } from "../../i18n";
import { useTableState } from "../../hooks";
import ActiveFilters from "../MaterialReactTable/ActiveFilters";

const SEARCH_DEBOUNCE_MS = 500;

const MatReactTableServer = ({
    data,
    columns,
    loading,
    rowActions,
    onRowClick,
    toolBarComponents,
    noRowsMessage,
    noRecordsMessage,
    // Row selection
    rowSelection,
    onSelectionChange,
    enableRowSelection,
    muiCheckboxProps,
    getRowId,
    // Server-side pagination
    rowCount,
    page,
    pageSize,
    setPage,
    setPageSize,
    // Server-side search
    setSearchQuery,
    initialState,
    selectionResetToken,
    getSubRows,
    // Opt-in persistence of column layout + global search across navigation
    persistKey
}) => {
    const theme = useTheme();
    const searchDebounceRef = useRef(null);

    // The search debounce fires ~500ms after the last keystroke. Without this,
    // typing and then navigating away lands a setSearchQuery/setPage on an
    // unmounted component.
    useEffect(
        () => () => {
            if (searchDebounceRef.current)
                clearTimeout(searchDebounceRef.current);
        },
        []
    );

    const noRecordsMsg =
        noRecordsMessage || noRowsMessage || defaultMessages.noRecords;

    const {
        persisted: statePersisted,
        slices: persistedSlices,
        handlers: persistedHandlers
    } = useTableState(persistKey, columns, {
        columnVisibility: initialState?.columnVisibility,
        columnPinning: {
            right: ["mrt-row-actions"],
            ...(initialState?.columnPinning || {})
        }
    });

    const globalFilterState = persistedSlices.globalFilter ?? "";

    const [rowSelectionState, setRowSelectionState] = useState(
        initialState?.rowSelection ?? {}
    );
    const rowSelectionRef = useRef(initialState?.rowSelection ?? {});

    useEffect(() => {
        if (!selectionResetToken) return;
        rowSelectionRef.current = {};
        setRowSelectionState({});
    }, [selectionResetToken]);

    const renderRowSelectCheckbox = useMemo(
        () =>
            ({ row }) => {
                const isSelectable = enableRowSelection
                    ? enableRowSelection(row)
                    : true;

                const customCheckboxProps = muiCheckboxProps
                    ? muiCheckboxProps({ row })
                    : {};

                if (!isSelectable) {
                    if (customCheckboxProps?.sx?.display === "none")
                        return null;
                    return (
                        <Checkbox
                            disabled
                            checked={false}
                            sx={{
                                "&.Mui-disabled": {
                                    color: theme.palette.action.disabled
                                },
                                ...customCheckboxProps.sx
                            }}
                            {...customCheckboxProps}
                        />
                    );
                }

                return (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                        aria-label="Select row"
                        sx={customCheckboxProps.sx}
                        {...customCheckboxProps}
                    />
                );
            },
        [enableRowSelection, muiCheckboxProps, theme]
    );

    const handleRowSelectionChange = useCallback(
        (updater) => {
            const newSelection =
                typeof updater === "function"
                    ? updater(rowSelectionRef.current)
                    : updater;
            rowSelectionRef.current = newSelection;
            setRowSelectionState(newSelection);
        },
        []
    );

    const handleGlobalFilterChange = useCallback(
        (value) => {
            const next = value ?? "";
            if (statePersisted) persistedHandlers.onGlobalFilterChange(next);
            if (!setSearchQuery) return;
            if (searchDebounceRef.current)
                clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => {
                setSearchQuery(next);
                setPage?.(0);
            }, SEARCH_DEBOUNCE_MS);
        },
        [setSearchQuery, setPage, statePersisted, persistedHandlers]
    );

    const handlePaginationChange = useCallback(
        (updater) => {
            const newPagination =
                typeof updater === "function"
                    ? updater({ pageIndex: page, pageSize })
                    : updater;

            if (newPagination.pageSize !== pageSize) {
                try {
                    localStorage.setItem(
                        "cvTablePageSize",
                        JSON.stringify(newPagination.pageSize)
                    );
                } catch (_) {}
                setPageSize?.(newPagination.pageSize);
                setPage?.(0);
            } else if (newPagination.pageIndex !== page) {
                setPage?.(newPagination.pageIndex);
            }
        },
        [page, pageSize, setPage, setPageSize]
    );

    const muiTableBodyRowProps = useCallback(
        ({ row }) => {
            const isGroupRow = row.original.isGroup;
            const isSelectable = enableRowSelection
                ? enableRowSelection(row)
                : true;
            const isClickable = onRowClick && isSelectable;
            const isSelectableRow = rowSelection && isSelectable;

            return {
                onClick: (event) => {
                    if (isGroupRow) {
                        row.getToggleExpandedHandler()(event);
                    } else if (isClickable) {
                        onRowClick(row.original);
                    } else if (isSelectableRow) {
                        row.getToggleSelectedHandler()(event);
                    }
                },
                sx: {
                    ...tableStyles.tableBodyRow,
                    cursor:
                        isGroupRow || isClickable || isSelectableRow
                            ? "pointer"
                            : "default",
                    "&:hover": {
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05
                        )
                    }
                }
            };
        },
        [enableRowSelection, onRowClick, rowSelection, theme]
    );

    const renderEmptyRowsFallback = useCallback(
        () => (
            <Box sx={tableStyles.emptyContainer}>
                <Typography sx={tableStyles.emptyText}>
                    {noRecordsMsg}
                </Typography>
            </Box>
        ),
        [noRecordsMsg]
    );

    const tableConfig = useMemo(() => {
        const config = {
            columns,
            data: data || [],

            // Server-side mode
            manualPagination: true,
            rowCount: rowCount ?? 0,
            onPaginationChange: handlePaginationChange,
            onGlobalFilterChange: handleGlobalFilterChange,

            defaultColumn: {
                sortingFn: "alphanumeric"
            },
            enableColumnFilterModes: false,
            enableClickToCopy: false,
            enableColumnOrdering: true,
            enableFacetedValues: false,
            enableGlobalFilter: true,
            enableRowSelectionOnClick: true,
            enableRowActions: !!rowActions,
            enableRowSelection: !!rowSelection,
            ...(getRowId && { getRowId }),
            layoutMode: "grid",
            enableColumnResizing: true,
            columnResizeMode: "onChange",
            enablePagination: true,

            muiTableBodyRowProps,

            muiTablePaperProps: { sx: tableStyles.tablePaper },
            muiTableProps: { sx: tableStyles.table },
            muiTableHeadProps: { sx: tableStyles.tableHead },

            muiTableHeadCellProps: ({ column }) => ({
                sx: {
                    ...tableStyles.tableHeadCell,
                    fontSize: "1rem",
                    fontFamily: theme.typography.fontFamily,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    backgroundColor: theme.palette.background.paper,
                    // Highlight headers of columns that currently have a filter
                    ...(column.getIsFiltered() && {
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderBottom: `2px solid ${theme.palette.primary.main}`
                    })
                }
            }),

            muiTableBodyCellProps: {
                sx: {
                    ...tableStyles.tableBodyCell,
                    fontSize: "1rem",
                    fontFamily: theme.typography.fontFamily
                }
            },

            muiSearchTextFieldProps: {
                placeholder: strings("search"),
                sx: tableStyles.searchTextField
            },

            muiPaginationProps: {
                rowsPerPageOptions: [25, 50, 100, 250],
                sx: tableStyles.pagination,
                showFirstButton: true,
                showLastButton: true
            },

            muiCheckboxProps: {
                sx: tableStyles.selectionCheckbox
            },

            muiIconButtonProps: {
                sx: tableStyles.toolbarButtons
            },

            muiTopToolbarProps: { sx: tableStyles.topToolbar },
            muiBottomToolbarProps: { sx: tableStyles.bottomToolbar },
            muiTableContainerProps: { sx: tableStyles.tableContainer },

            muiLinearProgressProps: { color: "primary" },

            renderEmptyRowsFallback,

            initialState: {
                density: "compact",
                showGlobalFilter: true,
                showColumnFilters: false,
                columnPinning: { right: ["mrt-row-actions"] },
                pagination: {
                    pageSize: pageSize ?? 25,
                    pageIndex: page ?? 0
                },
                ...initialState
            },

            state: {
                isLoading: loading,
                rowSelection: rowSelectionState,
                pagination: { pageIndex: page ?? 0, pageSize: pageSize ?? 25 }
            },

            paginationDisplayMode: "pages",
            positionToolbarAlertBanner: "bottom"
        };

        if (rowActions) {
            config.renderRowActions = ({ row }) => rowActions(row);
        }

        if (rowSelection) {
            config.renderRowSelectCheckbox = renderRowSelectCheckbox;
            config.onRowSelectionChange = handleRowSelectionChange;
        }

        if (enableRowSelection) {
            config.enableRowSelection = enableRowSelection;
        }

        if (getSubRows) {
            config.getSubRows = getSubRows;
            config.enableExpanding = true;
            // filterFromLeafRows keeps parent group rows visible when a child matches.
            // Note: with manualPagination:true the rowCount stays server-sourced, so
            // pagination totals will not reflect the client-side filtered count.
            config.filterFromLeafRows = true;
        }

        config.displayColumnDefOptions = {
            "mrt-row-expand": {
                size: 40,
                minSize: 40,
                maxSize: 40,
                muiTableHeadCellProps: { sx: { padding: "0 4px" } },
                muiTableBodyCellProps: { sx: { padding: "0 4px" } }
            },
            "mrt-row-select": {
                size: 40,
                minSize: 40,
                maxSize: 40,
                muiTableHeadCellProps: { sx: { padding: "0 4px" } },
                muiTableBodyCellProps: { sx: { padding: "0 4px" } }
            },
            "mrt-row-actions": {
                header: "Actions",
                size: 160,
                minSize: 160,
                muiTableHeadCellProps: { align: "center" },
                muiTableBodyCellProps: { align: "center", sx: { overflow: "visible" } }
            }
        };

        // Persisted view state + controlled global search. Column filters and
        // sorting here act on the currently-loaded page (client-side), but are
        // still controlled so they survive navigation. Each controlled slice
        // needs its on*Change handler or MRT freezes it.
        if (statePersisted) {
            config.state = {
                ...config.state,
                globalFilter: globalFilterState,
                columnFilters: persistedSlices.columnFilters,
                sorting: persistedSlices.sorting,
                columnVisibility: persistedSlices.columnVisibility,
                columnOrder: persistedSlices.columnOrder,
                columnPinning: persistedSlices.columnPinning
            };
            if (rowActions) {
                const right = new Set([
                    ...(persistedSlices.columnPinning?.right || []),
                    "mrt-row-actions"
                ]);
                config.state.columnPinning = {
                    ...persistedSlices.columnPinning,
                    right: Array.from(right)
                };
            }
            config.onColumnFiltersChange =
                persistedHandlers.onColumnFiltersChange;
            config.onSortingChange = persistedHandlers.onSortingChange;
            config.onColumnVisibilityChange =
                persistedHandlers.onColumnVisibilityChange;
            config.onColumnOrderChange = persistedHandlers.onColumnOrderChange;
            config.onColumnPinningChange =
                persistedHandlers.onColumnPinningChange;
        }

        return config;
    }, [
        columns,
        data,
        loading,
        rowCount,
        page,
        pageSize,
        rowSelection,
        rowActions,
        enableRowSelection,
        renderRowSelectCheckbox,
        handleRowSelectionChange,
        handlePaginationChange,
        handleGlobalFilterChange,
        muiTableBodyRowProps,
        renderEmptyRowsFallback,
        initialState,
        getRowId,
        rowSelectionState,
        getSubRows,
        statePersisted,
        persistedSlices,
        persistedHandlers,
        globalFilterState,
    ]);

    const table = useMaterialReactTable(tableConfig);

    // Notify callers of selection changes using MRT's row model — avoids the
    // fragile manual ID lookup that compared MRT index keys against data IDs.
    useEffect(() => {
        if (!onSelectionChange) return;
        const selected = table
            .getSelectedRowModel()
            .flatRows.filter((r) => !r.original.isGroup)
            .map((r) => r.original);
        onSelectionChange(selected);
    }, [rowSelectionState, onSelectionChange]);

    const selectedRows = table.getSelectedRowModel().flatRows || [];
    const selectedRowData = selectedRows.map((r) => r.original);
    const selectedRowCount = selectedRows.length;

    // Count of active column filters — surfaced as a badge so users can tell
    // filters are applied even when the filter panel is collapsed.
    const activeFilterCount = table.getState().columnFilters?.length || 0;

    return (
        <>
            <Box sx={tableStyles.toolbarContainer}>
                <MRT_GlobalFilterTextField table={table} />
                <Box sx={tableStyles.buttonGroup}>
                    {toolBarComponents && toolBarComponents(selectedRowData)}
                    <Badge
                        badgeContent={activeFilterCount}
                        color="primary"
                        overlap="circular"
                    >
                        <MRT_ToggleFiltersButton
                            table={table}
                            sx={tableStyles.toolbarButtons}
                        />
                    </Badge>
                    <MRT_ShowHideColumnsButton
                        table={table}
                        sx={tableStyles.toolbarButtons}
                    />
                </Box>
            </Box>

            {/* Active-filter chips bar */}
            <ActiveFilters table={table} />

            <Box sx={{ ...tableStyles.tableContainer, position: "relative" }}>
                <MRT_TableContainer table={table} />
            </Box>

            <Box sx={tableStyles.bottomSection}>
                <Box sx={tableStyles.selectionCount}>
                    {rowSelection && selectedRowCount > 0 && (
                        <Typography
                            variant="body2"
                            color="primary"
                            sx={{ fontWeight: "medium" }}
                        >
                            {selectedRowCount}{" "}
                            {selectedRowCount === 1 ? "row" : "rows"} selected
                        </Typography>
                    )}
                </Box>

                <Box sx={tableStyles.rowInfo} />

                <Box sx={tableStyles.paginationWrapper}>
                    <MRT_TablePagination table={table} />
                </Box>
            </Box>
        </>
    );
};

export default MatReactTableServer;
